/**
 * InstallerPortal — Sprint 5.2B.
 *
 * Portal do Técnico — assistente de instalação em campo, usado no
 * CELULAR. Renderizado na rota /installer, FORA do chrome do NOC.
 *
 * Wizard de 5 passos:
 *   1 WIFI       — SSID + senha do cliente
 *   2 EQUIPAMENTO— QR scanner (câmera) ou entrada manual
 *   3 CLIENTE    — busca IXC (nome / CPF / id)
 *   4 TEMPLATE   — Residencial / Empresarial / Custom
 *   5 FINALIZAR  — resumo + botão Instalar
 *
 * Ao finalizar cria um binding pendente; o auto-bind da Sprint 5.2A
 * fecha o vínculo quando o Sonoff der heartbeat.
 *
 * Mobile-first: botões grandes, barra de progresso, uma coisa por
 * vez. Funciona bem na largura de um celular.
 *
 * Deep-link: /installer?qr=25082200178100 pré-preenche o QR.
 */
import { useEffect, useState } from "react";
import {
  cancelInstallationSession,
  createInstallationSession,
  finishInstallationSession,
  getInstallationSession,
  getInstallerDeviceStatus,
  getInstallerCustomerSummary,
  login as apiLogin,
  logout as apiLogout,
  searchInstallerCustomers,
  updateInstallationSession,
} from "../lib/api.js";
import { clearSession, loadSession } from "../lib/auth.js";
import { t } from "../i18n/index.js";
import QrScannerModal, { isValidQrSerial } from "../components/QrScannerModal.jsx";
import {
  buildInfraProvisioning,
  DEFAULT_INSTALLER_TEMPLATE,
  INSTALLER_TEMPLATE_OPTIONS,
  isIxcProtected,
  ixcRestrictedMessage,
  stepsFor,
  templateSummary,
} from "../lib/installerProvisioning.js";

// PROVISIONING-FLOW-FRONTEND-1 — os passos do wizard são DINÂMICOS por tipo
// (Cliente vs Infraestrutura), computados dentro do componente (`steps`). O
// wizard começa no QR; o WiFi é configurado no portal local do Sonoff.

// Hotfix 5.2B-A — um request abortado (StrictMode remonta o efeito,
// navegação, troca de aba) NÃO é um erro de verdade. O AbortController
// rejeita a promise com AbortError; isso não deve virar mensagem de
// erro na tela ("The operation was aborted.").
function isAbortError(e) {
  if (!e) return false;
  if (e.name === "AbortError") return true;
  const msg = String(e.message || e).toLowerCase();
  return msg.includes("operation was aborted") || msg.includes("aborted");
}

export default function InstallerPortal() {
  // Deep-link ?qr=
  const urlParams = new URLSearchParams(window.location.search);
  const qrFromUrl = urlParams.get("qr") || "";

  const [session, setSession] = useState(null);
  const [step, setStep] = useState("equipment");
  const [creating, setCreating] = useState(true);
  const [createErr, setCreateErr] = useState(null);
  // Hotfix 5.2B-C — tela inicial: técnico confirma que já configurou
  // o WiFi no portal do Sonoff antes de começar o wizard.
  const [started, setStarted] = useState(false);
  // FIELD-LOGIN-UX-4 — login do técnico inline (Bearer). authSession = sessão de
  // usuário (Fernet), distinta da sessão de instalação. NUNCA usa recovery/
  // ADMIN_TOKEN aqui.
  const [authSession, setAuthSession] = useState(() => loadSession());
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginErr, setLoginErr] = useState(null);

  // Passo 1 — Equipamento
  const [qrSerial, setQrSerial] = useState(qrFromUrl);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Passo 2 — Cliente
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchErr, setSearchErr] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [logins, setLogins] = useState([]);
  const [contract, setContract] = useState(null);
  const [login, setLogin] = useState(null);

  // Passo 3 — Template
  // INSTALLER-TEMPLATES-UX-2 — padrão seguro: "custom" (Sem template).
  const [template, setTemplate] = useState(DEFAULT_INSTALLER_TEMPLATE);

  // PROVISIONING-FLOW-FRONTEND-1 — tipo de instalação + fluxo de Infraestrutura.
  const [deploymentType, setDeploymentType] = useState(null); // 'customer'|'infrastructure'
  const [infraName, setInfraName] = useState("");
  const [infraOpId, setInfraOpId] = useState("");
  const [infraCategory, setInfraCategory] = useState("Infraestrutura");
  const [infraHw, setInfraHw] = useState("core"); // 'core'|'energy'
  const [infraNotes, setInfraNotes] = useState("");
  const [infraLat, setInfraLat] = useState("");
  const [infraLng, setInfraLng] = useState("");
  const [infraAccuracy, setInfraAccuracy] = useState(null);
  const [infraLocSource, setInfraLocSource] = useState(null); // 'gps'|'manual'
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState(null);

  const INFRA_CATEGORIES = [
    "Infraestrutura", "Torre", "POP", "Backbone",
    "Equipamento crítico", "Teste/Laboratório",
  ];

  // Passo 4 — Finalizar
  const [finishing, setFinishing] = useState(false);
  const [finishErr, setFinishErr] = useState(null);
  const [finished, setFinished] = useState(null);
  // Hotfix 5.2B-B — o passo final renderiza a sessão LIDA DO BACKEND,
  // não o estado local React. verifiedSession guarda esse GET.
  const [verifiedSession, setVerifiedSession] = useState(null);
  const [verifying, setVerifying] = useState(false);
  // Hotfix 5.2B-C — status do equipamento (online, HB, fw).
  const [deviceStatus, setDeviceStatus] = useState(null);

  // Cria a sessão ao montar
  useEffect(() => {
    const ac = new AbortController();
    createInstallationSession({
      installer: "campo",
      qrSerial: qrFromUrl || null,
      signal: ac.signal,
    })
      .then((s) => setSession(s))
      .catch((e) => {
        // Hotfix 5.2B-A — request abortado não é erro de tela.
        if (isAbortError(e)) return;
        setCreateErr(e?.message || String(e));
      })
      .finally(() => {
        // Não tira o "creating" se foi abort — o efeito vai remontar.
        setCreating(false);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // INSTALLER-TEMPLATES-UX-2 — as opções da etapa Template agora são fixas no
  // frontend (INSTALLER_TEMPLATE_OPTIONS, ordem "Sem template" 1º). Não busca mais
  // /installer/templates (os 3 valores custom/residential/business são estáveis e
  // os textos/badges são de produto). O backend segue inalterado.

  // Hotfix 5.2B-B — ao entrar no PASSO FINAL, relê a sessão do backend.
  // O resumo e a validação do botão Instalar usam ESSES dados (o que
  // está realmente persistido), não o estado local React. Assim o
  // técnico vê exatamente o que o finish vai usar — e um campo que não
  // foi gravado aparece como pendente em vez de dar erro só no clique.
  useEffect(() => {
    if (step !== "finish" || !session?.id) return;
    const ac = new AbortController();
    setVerifying(true);
    getInstallationSession(session.id, { signal: ac.signal })
      .then((s) => {
        setVerifiedSession(s);
        setSession(s);
      })
      .catch((e) => {
        if (!isAbortError(e)) setFinishErr(e?.message || String(e));
      })
      .finally(() => setVerifying(false));
    return () => ac.abort();
  }, [step, session?.id]);

  // Hotfix 5.2B-C — depois de Instalar (binding criado), faz polling
  // do status do equipamento. Enquanto o Sonoff não der heartbeat,
  // bound=false; quando o auto-bind (S5.2A) fechar, mostra online +
  // fw + último HB. 10s é suave para o ritmo de uma instalação.
  useEffect(() => {
    if (!finished || !session?.id) return;
    let alive = true;
    const poll = () => {
      const ac = new AbortController();
      getInstallerDeviceStatus(session.id, { signal: ac.signal })
        .then((d) => { if (alive) setDeviceStatus(d); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [finished, session?.id]);

  // Patch helper — atualiza a sessão no backend
  // Hotfix 5.2B-B — o PATCH NÃO pode ser silencioso. Antes, um erro
  // aqui era engolido e o wizard avançava como se tivesse salvo —
  // o estado React mostrava os dados mas o backend nunca os recebia,
  // e o finish falhava com missing_qr_serial / missing_customer_id.
  // Agora patch() devolve true/false; o chamador só avança se true.
  const [patchErr, setPatchErr] = useState(null);
  const [patching, setPatching] = useState(false);

  const patch = async (fields) => {
    if (!session) return false;
    setPatching(true);
    setPatchErr(null);
    try {
      const updated = await updateInstallationSession(session.id, fields);
      setSession(updated);
      return true;
    } catch (e) {
      if (isAbortError(e)) return false;
      // Erro REAL de gravação — mostra na tela e NÃO deixa avançar.
      setPatchErr(e?.message || String(e));
      return false;
    } finally {
      setPatching(false);
    }
  };

  // ─── Navegação ─────────────────────────────────────────────────────
  // PROVISIONING-FLOW-FRONTEND-1 — passos dinâmicos por tipo. Infra pula
  // Cliente/Template (sem IXC).
  const steps = stepsFor(deploymentType);
  const stepLabels = deploymentType === "infrastructure"
    ? ["QR", "Tipo", "Infraestrutura", "Finalizar"]
    : ["QR", "Tipo", "Cliente", "Template", "Finalizar"];
  const stepIndex = steps.indexOf(step);
  const goNext = () => {
    const i = steps.indexOf(step);
    if (i >= 0 && i < steps.length - 1) setStep(steps[i + 1]);
  };
  const goBack = () => {
    const i = steps.indexOf(step);
    if (i > 0) setStep(steps[i - 1]);
  };

  // ─── Passo 1 — Equipamento ─────────────────────────────────────────
  const submitEquipment = async () => {
    const ok = await patch({ qr_serial: qrSerial.trim() });
    if (ok) setStep("type");
  };

  // ─── Passo Tipo — Cliente vs Infraestrutura ────────────────────────
  const chooseType = async (dt) => {
    setDeploymentType(dt);
    const rj = session?.raw_json || {};
    const prov = { ...(rj.provisioning || {}), deployment_type: dt };
    const ok = await patch({ raw_json: { ...rj, provisioning: prov } });
    if (ok) setStep(dt === "infrastructure" ? "infra" : "customer");
  };

  // ─── Passo Infraestrutura — captura de GPS + submit ────────────────
  const captureLocation = () => {
    setGeoErr(null);
    if (!navigator.geolocation) {
      setGeoErr("Geolocalização não suportada neste navegador.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setInfraLat(String(pos.coords.latitude));
        setInfraLng(String(pos.coords.longitude));
        setInfraAccuracy(pos.coords.accuracy);
        setInfraLocSource("gps");
        setGeoBusy(false);
      },
      (err) => {
        setGeoErr(
          "Não foi possível capturar a localização (verifique a permissão de GPS): " +
          (err?.message || `código ${err?.code}`)
        );
        setGeoBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };
  // Edição manual da coordenada → origem 'manual'.
  const onLatLngManual = (setter) => (v) => { setter(v); setInfraLocSource("manual"); };

  const submitInfra = async () => {
    const op = infraOpId.trim();
    if (!op) { setPatchErr("Informe o identificador operacional."); return; }
    const prov = buildInfraProvisioning({
      operationalIdentifier: op,
      hardwareCapability: infraHw,
      category: infraCategory,
      displayName: infraName,
      notes: infraNotes,
      lat: infraLat,
      lng: infraLng,
      accuracyM: infraAccuracy,
      locationSource: infraLocSource,
    });
    const rj = session?.raw_json || {};
    const ok = await patch({ raw_json: { ...rj, provisioning: prov } });
    if (ok) setStep("finish");
  };

  // ─── Passo 2 — Cliente ─────────────────────────────────────────────
  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchErr(null);
    setSearchResults([]);
    try {
      const data = await searchInstallerCustomers(searchQ.trim(), { limit: 20 });
      let results = [];
      if (Array.isArray(data)) results = data;
      else if (data && Array.isArray(data.results)) results = data.results;
      else if (data && Array.isArray(data.customers)) results = data.customers;
      setSearchResults(results);
    } catch (e) {
      if (isAbortError(e)) return;
      // PROVISIONING-FLOW-FRONTEND-1 — IXC fechado (401) → mensagem amigável,
      // sem reabrir IXC. Sugere o fluxo de Infraestrutura.
      if (isIxcProtected(e)) {
        // FIELD-LOGIN-UX-4 — mensagem conforme logado (403) ou público (401);
        // NÃO reabre IXC.
        setSearchErr(ixcRestrictedMessage(!!authSession?.token));
      } else {
        setSearchErr(e?.message || String(e));
      }
    } finally {
      setSearching(false);
    }
  };

  const selectCustomer = async (c) => {
    setCustomer(c);
    setContracts([]);
    setLogins([]);
    setContract(null);
    setLogin(null);
    try {
      const summary = await getInstallerCustomerSummary(c.id);
      setContracts(summary.contracts || []);
      setLogins(summary.logins || []);
    } catch {
      // segue sem contrato/login — opcional
    }
  };

  const submitCustomer = async () => {
    const ok = await patch({
      customer_id: customer.id,
      contract_id: contract?.id ?? null,
      radius_id: login?.id ?? null,
      pppoe_login: login?.login ?? null,
      // PROVISIONING-FLOW-FRONTEND-1 — marca deployment_type=customer (não quebra IXC)
      raw_json: {
        customer, contract, radius: login,
        provisioning: { deployment_type: "customer" },
      },
    });
    if (ok) goNext();
  };

  // ─── Passo 3 — Template ────────────────────────────────────────────
  const submitTemplate = async () => {
    const ok = await patch({ template_id: template });
    if (ok) goNext();
  };

  // ─── Passo 4 — Finalizar ───────────────────────────────────────────
  const doFinish = async () => {
    setFinishing(true);
    setFinishErr(null);
    try {
      const res = await finishInstallationSession(session.id);
      setFinished(res);
    } catch (e) {
      // Hotfix 5.2B-A — request abortado não vira erro de tela.
      if (!isAbortError(e)) setFinishErr(e?.message || String(e));
    } finally {
      setFinishing(false);
    }
  };

  const doCancel = async () => {
    if (session) {
      try {
        await cancelInstallationSession(session.id);
      } catch {
        // ignora
      }
    }
    window.location.reload();
  };

  // ─── FIELD-LOGIN-UX-4 — login/logout do técnico (Bearer) ───────────────
  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setLoginErr(null);
    if (!loginEmail.trim() || !loginPassword) {
      setLoginErr("Informe e-mail e senha.");
      return;
    }
    setLoginBusy(true);
    try {
      const sess = await apiLogin(loginEmail.trim(), loginPassword); // salva Bearer
      setAuthSession(sess);
      // Re-cria a sessão de instalação AGORA com o Bearer → herda tenant_id.
      try {
        const fresh = await createInstallationSession({
          installer: sess?.fullName || sess?.email || loginEmail.trim(),
          qrSerial: qrSerial || null,
        });
        setSession(fresh);
      } catch {
        /* mantém a sessão atual se a recriação falhar */
      }
      setShowLogin(false);
      setStarted(true);
    } catch (err) {
      setLoginErr(
        err?.message || "Não foi possível entrar. Verifique e-mail e senha."
      );
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch {
      /* best-effort */
    }
    clearSession();
    setAuthSession(null);
    window.location.reload();
  };

  // Barra discreta do técnico logado (topo da landing e do wizard).
  const techBar = authSession?.token ? (
    <div className="flex items-center justify-between gap-2 bg-noc-panel border border-noc-border rounded-lg px-3 py-2 mb-3 text-xs">
      <div className="truncate">
        <span className="text-noc-muted">Técnico:</span>{" "}
        <b>{authSession.fullName || authSession.email}</b>
        {authSession.tenantName && (
          <span className="text-noc-muted"> · {authSession.tenantName}</span>
        )}
      </div>
      <button
        type="button"
        onClick={handleLogout}
        className="text-noc-accent underline shrink-0"
      >
        Sair
      </button>
    </div>
  ) : null;

  // ─── Render ────────────────────────────────────────────────────────
  if (creating) {
    return (
      <Shell>
        <p className="text-center text-noc-muted text-sm py-12">
          {t("installer.creating")}
        </p>
      </Shell>
    );
  }
  if (createErr) {
    return (
      <Shell>
        <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-3">
          {createErr}
        </div>
      </Shell>
    );
  }

  // Hotfix 5.2B-C — tela inicial: o técnico configura o WiFi no
  // portal do Sonoff (IPWD-XXXX / 192.168.4.1) e só então começa o
  // wizard. Não há passo WiFi aqui.
  if (!started) {
    return (
      <Shell>
        {/* FIELD-LOGIN-UX-3/4 — tela inicial de DUAS VIAS + login do técnico */}
        {techBar}
        <section className="space-y-4">
          <div className="text-center space-y-1 pt-2">
            <h2 className="text-lg font-bold text-noc-text">
              IP WatchDog — Instalação em Campo
            </h2>
            <p className="text-sm text-noc-muted">
              Escolha como deseja iniciar a instalação.
            </p>
          </div>

          {/* Via 1 — Entrar como técnico (login Bearer inline) */}
          {authSession?.token ? (
            <div className="w-full p-4 rounded-xl border border-noc-ok/30 bg-noc-ok/10">
              <div className="text-sm font-semibold text-noc-ok">
                ✓ Logado como {authSession.fullName || authSession.email}
              </div>
              <BigButton onClick={() => setStarted(true)}>
                Continuar a instalação
              </BigButton>
            </div>
          ) : showLogin ? (
            <form
              onSubmit={handleLogin}
              className="w-full p-4 rounded-xl border border-noc-border bg-noc-panel space-y-3"
            >
              <div className="text-base font-semibold">👷 Entrar como técnico</div>
              <Field label="E-mail">
                <input
                  type="email" autoComplete="username" value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="voce@empresa.com"
                  className="installer-input"
                />
              </Field>
              <Field label="Senha">
                <input
                  type="password" autoComplete="current-password" value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="installer-input"
                />
              </Field>
              {loginErr && (
                <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                  {loginErr}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowLogin(false); setLoginErr(null); }}
                  className="px-4 py-3 rounded-lg border border-noc-border text-noc-muted text-sm"
                >
                  Voltar
                </button>
                <BigButton onClick={handleLogin} disabled={loginBusy}>
                  {loginBusy ? "Entrando…" : "Entrar"}
                </BigButton>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setShowLogin(true)}
              className="w-full text-left p-4 rounded-xl border border-noc-border bg-noc-panel active:bg-noc-accent/10"
            >
              <div className="text-base font-semibold flex items-center gap-2">
                👷 Entrar como técnico
              </div>
              <p className="text-[12px] text-noc-muted mt-1 leading-relaxed">
                Use sua conta de técnico para registrar instalações vinculadas à
                empresa.
              </p>
            </button>
          )}

          {/* Via 2 — Continuar com QR/link (fluxo público atual) */}
          <button
            type="button"
            onClick={() => setStarted(true)}
            className="w-full text-left p-4 rounded-xl border border-noc-accent/40 bg-noc-accent/10 active:bg-noc-accent/20"
          >
            <div className="text-base font-semibold flex items-center gap-2 text-noc-accent">
              📷 Continuar com QR/link de instalação
            </div>
            <p className="text-[12px] text-noc-muted mt-1 leading-relaxed">
              Use o QR Code ou link de instalação para continuar sem login.
            </p>
          </button>

          {/* Aviso discreto sobre IXC autenticado */}
          <p className="text-[11px] text-noc-muted/80 leading-relaxed text-center px-2">
            Cliente IXC exige acesso autenticado. Infraestrutura/Torre pode ser
            instalada pelo fluxo com QR.
          </p>

          {/* Lembrete de WiFi do Sonoff (operacional, antes do wizard) */}
          <details className="text-xs text-noc-muted/80 border-t border-noc-border/40 pt-3">
            <summary className="cursor-pointer text-noc-muted">
              📡 Antes de começar: configure o WiFi no equipamento
            </summary>
            <p className="mt-2 leading-relaxed">{t("installer.welcomeWifiHint")}</p>
            <ol className="text-[11px] text-noc-muted/90 space-y-1.5 pl-4 list-decimal mt-2">
              <li>{t("installer.welcomeStep1")}</li>
              <li>{t("installer.welcomeStep2")}</li>
              <li>{t("installer.welcomeStep3")}</li>
            </ol>
          </details>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* FIELD-LOGIN-UX-4 — barra do técnico logado (topo do wizard) */}
      {techBar}
      {/* Barra de progresso */}
      <ProgressBar stepIndex={stepIndex} labels={stepLabels} />

      {/* Hotfix 5.2B-B — erro ao salvar um passo no backend.
          Visível em qualquer passo; o passo não avança enquanto
          o save falhar (antes o erro era engolido silenciosamente). */}
      {patchErr && (
        <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 mb-3">
          ⚠ {t("installer.saveFailed")}: {patchErr}
        </div>
      )}


      {/* Passo 1 — EQUIPAMENTO */}
      {step === "equipment" && (
        <StepCard title={t("installer.step2Title")} icon="📷">
          <Field label={t("installer.qrSerial")}>
            <input
              type="text"
              value={qrSerial}
              onChange={(e) => setQrSerial(e.target.value)}
              placeholder="25082200178100"
              className="installer-input font-mono"
            />
          </Field>
          <BigButton variant="secondary" onClick={() => setScannerOpen(true)}>
            📷 {t("installer.scanQr")}
          </BigButton>
          {qrSerial && !isValidQrSerial(qrSerial) && (
            <p className="text-[11px] text-noc-warn">
              ⚠ {t("installer.qrInvalid")}
            </p>
          )}
          <div className="flex gap-2">
            <BackButton onClick={goBack} />
            <BigButton
              onClick={submitEquipment}
              disabled={!isValidQrSerial(qrSerial) || patching}
            >
              {t("installer.next")}
            </BigButton>
          </div>
        </StepCard>
      )}

      {/* Passo Tipo — CLIENTE vs INFRAESTRUTURA (PROVISIONING-FLOW-FRONTEND-1) */}
      {step === "type" && (
        <StepCard title="O que você está instalando?" icon="🧭">
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => chooseType("customer")}
              disabled={patching}
              className="w-full text-left p-4 rounded-lg border border-noc-border bg-noc-bg/40 active:bg-noc-accent/10 disabled:opacity-40"
            >
              <div className="text-base font-semibold">👤 Cliente</div>
              <div className="text-[11px] text-noc-muted mt-1">
                Vincula ao cliente/login do IXC (busca por nome, CPF ou id).
              </div>
            </button>
            <button
              type="button"
              onClick={() => chooseType("infrastructure")}
              disabled={patching}
              className="w-full text-left p-4 rounded-lg border border-noc-border bg-noc-bg/40 active:bg-noc-accent/10 disabled:opacity-40"
            >
              <div className="text-base font-semibold">🗼 Infraestrutura/Torre</div>
              <div className="text-[11px] text-noc-muted mt-1">
                Ponto de rede (torre, POP, backbone, equipamento crítico). Sem
                cliente IXC — usa identificador manual + coordenada de campo.
              </div>
            </button>
          </div>
          <div className="flex gap-2">
            <BackButton onClick={() => setStep("equipment")} />
          </div>
        </StepCard>
      )}

      {/* Passo INFRAESTRUTURA — sem IXC (PROVISIONING-FLOW-FRONTEND-1) */}
      {step === "infra" && (
        <StepCard title="Infraestrutura / Torre" icon="🗼">
          <Field label="Nome do ponto">
            <input
              type="text" value={infraName}
              onChange={(e) => setInfraName(e.target.value)}
              placeholder="Torre Centro"
              className="installer-input"
            />
          </Field>
          <Field label="Identificador operacional *">
            <input
              type="text" value={infraOpId}
              onChange={(e) => setInfraOpId(e.target.value)}
              placeholder="TORRE-CENTRO"
              className="installer-input font-mono"
            />
          </Field>
          <Field label="Categoria">
            <select
              value={infraCategory}
              onChange={(e) => setInfraCategory(e.target.value)}
              className="installer-input"
            >
              {INFRA_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Modelo">
            <div className="flex gap-2">
              {[["core", "Core (sem medição)"], ["energy", "Energy (INA226)"]].map(([v, lbl]) => (
                <button
                  key={v} type="button" onClick={() => setInfraHw(v)}
                  className={`flex-1 py-2 rounded-lg border text-sm ${
                    infraHw === v
                      ? "border-noc-accent/30 bg-noc-accent/10 text-noc-accent font-semibold"
                      : "border-noc-border text-noc-muted"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </Field>

          <BigButton variant="secondary" onClick={captureLocation} disabled={geoBusy}>
            {geoBusy ? "📍 Capturando…" : "📍 Capturar localização atual"}
          </BigButton>
          {geoErr && (
            <div className="text-[11px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {geoErr}
            </div>
          )}
          <div className="flex gap-2">
            <Field label="Latitude">
              <input
                type="text" inputMode="decimal" value={infraLat}
                onChange={(e) => onLatLngManual(setInfraLat)(e.target.value)}
                placeholder="-10.000000"
                className="installer-input font-mono"
              />
            </Field>
            <Field label="Longitude">
              <input
                type="text" inputMode="decimal" value={infraLng}
                onChange={(e) => onLatLngManual(setInfraLng)(e.target.value)}
                placeholder="-39.000000"
                className="installer-input font-mono"
              />
            </Field>
          </div>
          <div className="text-[11px] text-noc-muted">
            Origem: {infraLocSource ? <b>{infraLocSource}</b> : "—"}
            {infraAccuracy != null && <> · precisão ~{Math.round(infraAccuracy)} m</>}
          </div>
          <Field label="Observação">
            <input
              type="text" value={infraNotes}
              onChange={(e) => setInfraNotes(e.target.value)}
              placeholder="Nobreak da torre, etc."
              className="installer-input"
            />
          </Field>
          <div className="flex gap-2">
            <BackButton onClick={() => setStep("type")} />
            <BigButton onClick={submitInfra} disabled={!infraOpId.trim() || patching}>
              {t("installer.next")}
            </BigButton>
          </div>
        </StepCard>
      )}

      {/* Passo 2 — CLIENTE */}
      {step === "customer" && (
        <StepCard title={t("installer.step3Title")} icon="👤">
          {!customer && (
            <>
              <Field label={t("installer.searchLabel")}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                    placeholder={t("installer.searchPlaceholder")}
                    className="installer-input flex-1"
                  />
                  <button
                    type="button"
                    onClick={runSearch}
                    disabled={searching || !searchQ.trim()}
                    className="px-4 rounded-lg bg-noc-accent text-noc-bg text-sm font-semibold disabled:opacity-40"
                  >
                    {searching ? "…" : "🔍"}
                  </button>
                </div>
              </Field>
              {searchErr && (
                <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                  {searchErr}
                </div>
              )}
              {searchResults.length === 0 && !searching && searchQ && !searchErr && (
                <p className="text-xs text-noc-warn">
                  {t("installer.noResults")}
                </p>
              )}
              <div className="space-y-2">
                {searchResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectCustomer(c)}
                    className="w-full text-left p-3 rounded-lg border border-noc-border bg-noc-bg/40 active:bg-noc-accent/10"
                  >
                    <div className="font-mono text-noc-accent text-xs">
                      #{c.id}
                    </div>
                    <div className="text-sm">{c.razao || "—"}</div>
                    {c.cnpj_cpf && (
                      <div className="text-[11px] text-noc-muted">
                        {c.cnpj_cpf}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {customer && (
            <>
              <div className="p-3 rounded-lg border border-noc-accent/30 bg-noc-accent/10">
                <div className="text-sm font-semibold">{customer.razao}</div>
                <div className="text-[11px] text-noc-muted font-mono">
                  #{customer.id}
                </div>
              </div>

              {contracts.length > 0 && (
                <Field label={t("installer.contract")}>
                  <select
                    value={contract?.id || ""}
                    onChange={(e) =>
                      setContract(
                        contracts.find((c) => String(c.id) === e.target.value)
                      )
                    }
                    className="installer-input"
                  >
                    <option value="">{t("installer.selectOne")}</option>
                    {contracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        #{c.id} · {c.contrato || c.plano || ""}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {logins.length > 0 && (
                <Field label={t("installer.pppoeLogin")}>
                  <select
                    value={login?.id || ""}
                    onChange={(e) =>
                      setLogin(
                        logins.find((l) => String(l.id) === e.target.value)
                      )
                    }
                    className="installer-input"
                  >
                    <option value="">{t("installer.selectOne")}</option>
                    {logins.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.login}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <button
                type="button"
                onClick={() => setCustomer(null)}
                className="text-[11px] text-noc-accent underline"
              >
                {t("installer.changeCustomer")}
              </button>
            </>
          )}

          <div className="flex gap-2">
            <BackButton onClick={goBack} />
            <BigButton onClick={submitCustomer} disabled={!customer || patching}>
              {t("installer.next")}
            </BigButton>
          </div>
        </StepCard>
      )}

      {/* Passo 3 — TEMPLATE (INSTALLER-TEMPLATES-UX-2: "Sem template" 1º/padrão) */}
      {step === "template" && (
        <StepCard title={t("installer.step4Title")} icon="🎛">
          <div className="space-y-2">
            {INSTALLER_TEMPLATE_OPTIONS.map((tpl) => {
              const active = template === tpl.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => setTemplate(tpl.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    active
                      ? tpl.recommended
                        ? "border-noc-ok/40 bg-noc-ok/10"
                        : "border-noc-accent/30 bg-noc-accent/10"
                      : "border-noc-border bg-noc-bg/40"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`w-3.5 h-3.5 rounded-full border shrink-0 ${
                        active
                          ? tpl.recommended
                            ? "bg-noc-ok/20 border-noc-ok/50"
                            : "bg-noc-accent/10 border-noc-accent/30"
                          : "border-noc-muted"
                      }`}
                    />
                    <span className="font-semibold text-sm">{tpl.name}</span>
                    {tpl.badge && (
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                          tpl.recommended
                            ? "border-noc-ok/40 bg-noc-ok/10 text-noc-ok"
                            : "border-noc-warn/40 bg-noc-warn/10 text-noc-warn"
                        }`}
                      >
                        {tpl.badge}
                      </span>
                    )}
                  </div>
                  {tpl.recommended && (
                    <p className="text-[10px] text-noc-ok/90 mt-1 ml-5.5">
                      ✓ Recomendado em caso de dúvida
                    </p>
                  )}
                  <p className="text-[11px] text-noc-muted/80 mt-1 ml-5.5">
                    {tpl.desc}
                  </p>
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <BackButton onClick={goBack} />
            <BigButton onClick={submitTemplate} disabled={patching}>
              {t("installer.next")}
            </BigButton>
          </div>
        </StepCard>
      )}

      {/* Passo 4 — FINALIZAR */}
      {step === "finish" && (
        <StepCard title={t("installer.step5Title")} icon="✅">
          {!finished && (() => {
            // Hotfix 5.2B-B — fonte da verdade: a sessão do backend.
            const s = verifiedSession || {};
            // PROVISIONING-FLOW-FRONTEND-1 — fluxo Infra (sem IXC) vs Cliente.
            const prov = (s.raw_json && s.raw_json.provisioning) || {};
            const isInfra = prov.deployment_type === "infrastructure";
            const missing = [];
            if (!s.qr_serial) missing.push(t("installer.qrSerial"));
            if (isInfra) {
              if (!prov.operational_identifier) missing.push("Identificador operacional");
            } else {
              if (!s.customer_id) missing.push(t("installer.customer"));
              if (!s.contract_id) missing.push(t("installer.contract"));
            }
            const ready = missing.length === 0;
            // INSTALLER-TEMPLATES-UX-2 — resumo claro da configuração inicial.
            const tpl = templateSummary(s.template_id);
            const loc = prov.location || {};

            return (
              <>
                {verifying && (
                  <div className="text-xs text-noc-muted">
                    {t("common.loading")}
                  </div>
                )}

                <dl className="space-y-1.5 text-sm">
                  <SummaryRow
                    label={t("installer.qrSerial")}
                    value={s.qr_serial || "—"}
                    mono
                  />
                  {/* INSTALLER-FINALIZE-FIX-1A — técnico/empresa também no resumo */}
                  <SummaryRow
                    label="Técnico"
                    value={
                      authSession?.token
                        ? authSession.fullName || authSession.email
                        : "Não autenticado / QR público"
                    }
                  />
                  {authSession?.tenantName && (
                    <SummaryRow label="Empresa" value={authSession.tenantName} />
                  )}
                  {isInfra ? (
                    <>
                      <SummaryRow label="Tipo" value="Infraestrutura/Torre" />
                      <SummaryRow label="Identificador" value={prov.operational_identifier || "—"} mono />
                      <SummaryRow label="Categoria" value={prov.category || "—"} />
                      <SummaryRow label="Modelo" value={prov.hardware_capability === "energy" ? "Energy (INA226)" : "Core"} />
                      <SummaryRow
                        label="Localização"
                        value={loc.lat != null && loc.lng != null
                          ? `${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)} (${loc.location_source || "—"})`
                          : "—"}
                      />
                    </>
                  ) : (
                    <>
                      <SummaryRow
                        label={t("installer.customer")}
                        value={s.customer_id ? `#${s.customer_id}` : "—"}
                      />
                      <SummaryRow
                        label={t("installer.contract")}
                        value={s.contract_id ? `#${s.contract_id}` : "—"}
                      />
                      <SummaryRow
                        label={t("installer.pppoeLogin")}
                        value={s.pppoe_login || "—"}
                      />
                      <SummaryRow
                        label="Configuração inicial"
                        value={tpl.label}
                      />
                      {tpl.note && (
                        <div className="text-[11px] text-noc-ok/90 ml-0.5">
                          {tpl.note}
                        </div>
                      )}
                    </>
                  )}
                </dl>

                {/* Hotfix 5.2B-B — estado da sessão: pronta ou pendente */}
                {!verifying && ready && (
                  <div className="text-xs text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
                    ✓ {t("installer.sessionReady")}
                  </div>
                )}
                {!verifying && !ready && (
                  <div className="text-xs text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
                    ⚠ {t("installer.sessionIncomplete")}: {missing.join(", ")}
                  </div>
                )}

                {finishErr && (
                  <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                    {finishErr}
                  </div>
                )}

                <div className="flex gap-2">
                  <BackButton onClick={goBack} />
                  <BigButton
                    onClick={doFinish}
                    disabled={finishing || verifying || !ready}
                  >
                    {finishing
                      ? t("installer.installing")
                      : t("installer.install")}
                  </BigButton>
                </div>
              </>
            );
          })()}

          {finished && (
            <div className="space-y-3 text-center py-4">
              <div className="text-5xl">🎉</div>
              <div className="text-base font-semibold text-noc-ok">
                {t("installer.doneTitle")}
              </div>
              <p className="text-sm text-noc-muted">
                {t("installer.doneHelp")}
              </p>

              {/* Hotfix 5.2B-C — status do equipamento (polling 10s) */}
              <div className="text-left bg-noc-bg/40 border border-noc-border rounded-lg p-3 space-y-1.5">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-noc-faint">
                  {t("installer.deviceStatusTitle")}
                </div>
                {!deviceStatus || !deviceStatus.bound ? (
                  <div className="text-xs text-noc-warn">
                    ⏳ {t("installer.deviceWaiting")}
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-noc-muted">
                        {t("installer.deviceStatusLabel")}
                      </span>
                      <span
                        className={
                          deviceStatus.online
                            ? "text-noc-ok font-semibold"
                            : "text-noc-muted"
                        }
                      >
                        {deviceStatus.online
                          ? `● ${t("installer.deviceOnline")}`
                          : `○ ${t("installer.deviceOffline")}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-noc-muted">Device</span>
                      <span className="font-mono">
                        {deviceStatus.device_id || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-noc-muted">
                        {t("installer.deviceFw")}
                      </span>
                      <span className="font-mono">
                        {deviceStatus.fw_version || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-noc-muted">
                        {t("installer.deviceLastHb")}
                      </span>
                      <span>
                        {deviceStatus.last_heartbeat
                          ? new Date(
                              deviceStatus.last_heartbeat
                            ).toLocaleString("pt-BR")
                          : "—"}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="text-[11px] text-noc-muted/70 font-mono">
                binding: {finished.binding_id}
              </div>
              <BigButton onClick={() => window.location.reload()}>
                {t("installer.newInstall")}
              </BigButton>
            </div>
          )}
        </StepCard>
      )}

      {/* Botão cancelar — sempre disponível, exceto na tela final */}
      {!finished && (
        <button
          type="button"
          onClick={doCancel}
          className="w-full text-center text-[11px] text-noc-muted/60 mt-4 underline"
        >
          {t("installer.cancel")}
        </button>
      )}

      {/* Scanner QR */}
      {scannerOpen && (
        <QrScannerModal
          onDetected={(value) => {
            setQrSerial(value);
            setScannerOpen(false);
          }}
          onManual={() => setScannerOpen(false)}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponentes
// ─────────────────────────────────────────────────────────────────────

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-noc-bg text-noc-text">
      <header className="bg-noc-panel border-b border-noc-border px-4 py-3">
        <h1 className="text-base font-bold text-noc-accent">
          ⚡ {t("installer.appTitle")}
        </h1>
        <p className="text-[11px] text-noc-muted">{t("installer.appSub")}</p>
      </header>
      <main className="max-w-md mx-auto p-4">{children}</main>
    </div>
  );
}

function ProgressBar({ stepIndex, labels = [] }) {
  // PROVISIONING-FLOW-FRONTEND-1 — labels dinâmicos (Cliente vs Infra).
  return (
    <div className="mb-4">
      <div className="flex gap-1">
        {labels.map((lbl, i) => (
          <div key={lbl} className="flex-1">
            <div
              className={`h-1.5 rounded-full ${
                i <= stepIndex ? "bg-noc-accent/10" : "bg-noc-border"
              }`}
            />
            <div
              className={`text-[9px] mt-1 text-center ${
                i === stepIndex
                  ? "text-noc-accent font-semibold"
                  : "text-noc-muted/60"
              }`}
            >
              {lbl}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepCard({ title, icon, children }) {
  return (
    <section className="bg-noc-panel border border-noc-border rounded-lg p-4 space-y-3">
      <h2 className="text-sm font-semibold flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-noc-muted">{label}</span>
      {children}
    </label>
  );
}

function BigButton({ children, onClick, disabled, variant = "primary" }) {
  const base =
    "flex-1 py-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40";
  const styles =
    variant === "secondary"
      ? "border border-noc-accent/30 text-noc-accent active:bg-noc-accent/10"
      : "bg-noc-accent text-noc-bg active:bg-noc-glow";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles}`}
    >
      {children}
    </button>
  );
}

function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-3 rounded-lg border border-noc-border text-noc-muted text-sm active:bg-noc-bg/60"
    >
      ←
    </button>
  );
}

function SummaryRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-2 border-b border-noc-border/40 pb-1">
      <dt className="text-noc-muted">{label}</dt>
      <dd className={mono ? "font-mono text-right" : "text-right"}>
        {value}
      </dd>
    </div>
  );
}
